export default {
  namespaced: true,
  state: {
    currentEmail: {
      from: null,
      to: [],
      cc: [],
      internetMessageId: null,
      subject: '',
      fromName: '',
      fromEmail: '',
      received: null,
      loggedInUser: null,
      dateTimeCreated: null
    },
    emailItem: null,
    accessToken: null,
    isLoading: false,
    userDetails: null
  },
  mutations: {
    SET_CURRENT_EMAIL(state, email) {
      state.currentEmail = email
    },
    SET_ACCESS_TOKEN(state, token) {
      state.accessToken = token
    },
    SET_LOADING(state, isLoading) {
      state.isLoading = isLoading
    },
    SET_USER_DETAILS(state, userDetails) {
      state.userDetails = userDetails;
    }
  },
  actions: {
    setLoggedInUserDetails({ commit }, userDetails) {
      commit('SET_USER_DETAILS', userDetails);
    },
    markEmailComplete({ commit }, payload) {
      console.log('Marking email as complete:', payload);
    },
    loadCurrentEmail({ commit }) {
      commit('SET_LOADING', true)
      try {
        // Check if Office.js is ready before accessing
        if (typeof Office !== "undefined" && Office.context?.mailbox?.item) {
          const item = Office.context.mailbox.item
          const email = {
            from: item.from,
            cc: item.cc,
            to: item.to,
            internetMessageId: item.internetMessageId,
            dateTimeCreated: item.dateTimeCreated,
            subject: item.subject,
            fromName: item.sender?.displayName || 'Unknown Sender',
            fromEmail: item.sender?.emailAddress || 'unknown@email.com',
            received: item.dateTimeModified,
            loggedInUser: Office.context.mailbox.userProfile.emailAddress
          }
          commit('SET_CURRENT_EMAIL', email)
        } else {
        }
      } catch (error) {
      } finally {
        commit('SET_LOADING', false)
      }
    },
    async getAccessToken({ commit }) {
      try {
        // Check if OfficeRuntime is available
        if (typeof OfficeRuntime !== "undefined" && OfficeRuntime.auth) {
          const token = await OfficeRuntime.auth.getAccessToken({ allowSignInPrompt: false })
          commit('SET_ACCESS_TOKEN', token)
          return token
        } else {
          throw new Error('OfficeRuntime.auth not available')
        }
      } catch (error) {
        // console.error('Failed to get access token:', error)
        // throw error
      }
    },
    
    /**
     * Convert current email to EML format and return as FormData
     * @returns {Promise<FormData>} FormData containing the EML file and attachments
     */
    async convertToEmlFormData({ state, dispatch }) {
      try {
        if (typeof Office === "undefined" || !Office.context?.mailbox?.item) {
          throw new Error("Office.js not available or no email item selected");
        }

        const item = Office.context.mailbox.item;
        
        // Get email data with HTML body and attachments
        const emailData = await new Promise((resolve, reject) => {
          try {
            const data = {
              messageId: item.internetMessageId || `<${Date.now()}@outlook.com>`,
              subject: item.subject || 'No Subject',
              from: {
                name: item.sender?.displayName || 'Unknown Sender',
                email: item.sender?.emailAddress || 'unknown@example.com'
              },
              to: (item.to || []).map(recipient => ({
                name: recipient.displayName || '',
                email: recipient.emailAddress
              })),
              cc: (item.cc || []).map(recipient => ({
                name: recipient.displayName || '',
                email: recipient.emailAddress
              })),
              date: item.dateTimeCreated || new Date(),
              body: '',
              attachments: []
            };

            // Get email body as HTML to preserve styles
            item.body.getAsync(Office.CoercionType.Html, (result) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                data.body = result.value;
              } else {
                data.body = '';
              }
              resolve(data);
            });

          } catch (error) {
            reject(error);
          }
        });

        // Get attachments if available
        const attachments = [];
        if (item.attachments && item.attachments.length > 0) {
          for (let i = 0; i < item.attachments.length; i++) {
            const attachment = item.attachments[i];
            try {
              const attachmentData = await new Promise((resolve, reject) => {
                if (attachment.attachmentType === Office.MailboxEnums.AttachmentType.File) {
                  // Use the mailbox item's method to get attachment content
                  item.getAttachmentContentAsync(attachment.id, (result) => {
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                      resolve({
                        id: attachment.id,
                        name: attachment.name,
                        contentType: attachment.contentType || 'application/octet-stream',
                        content: result.value.content,
                        size: attachment.size
                      });
                    } else {
                      console.warn(`Failed to get attachment content for ${attachment.name}:`, result.error);
                      resolve(null);
                    }
                  });
                } else {
                  // Handle item attachments (like embedded emails)
                  resolve({
                    id: attachment.id,
                    name: attachment.name,
                    contentType: 'message/rfc822',
                    content: null, // Item attachments can't be retrieved as content
                    size: attachment.size,
                    isItemAttachment: true
                  });
                }
              });
              
              if (attachmentData) {
                attachments.push(attachmentData);
              }
            } catch (error) {
              console.warn(`Error processing attachment ${attachment.name}:`, error);
            }
          }
        }

        emailData.attachments = attachments;
        
        // Create EML content using helper function
        const emlContent = createEmlContent(emailData);
        
        // Create FormData
        const formData = new FormData();
        const emlBlob = new Blob([emlContent], { type: 'message/rfc822' });
        
        //const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const now = new Date()
        const pad = (n) => n.toString().padStart(2, '0')
        const y = now.getFullYear()
        const m = pad(now.getMonth() + 1)
        const d = pad(now.getDate())
        const h = pad(now.getHours())
        const min = pad(now.getMinutes())
        const s = pad(now.getSeconds())
        const timestamp = `_${y}${m}${d}_${h}${min}${s}`
        
        const filename = `email_${timestamp}.eml`;
        
        // Add the main EML file
        formData.append('file', emlBlob, filename);
        formData.append('type', 'eml');
        formData.append('timestamp', new Date().toISOString());
        
        // Add individual attachments to FormData
        attachments.forEach((attachment, index) => {
          if (attachment.content && !attachment.isItemAttachment) {
            try {
              // Convert base64 content to blob
              const binaryString = atob(attachment.content);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              const attachmentBlob = new Blob([bytes], { type: attachment.contentType });
              formData.append(`attachment_${index}`, attachmentBlob, attachment.name);
            } catch (error) {
              console.warn(`Error processing attachment ${attachment.name} for FormData:`, error);
            }
          }
        });
        
        // Add metadata about attachments
        formData.append('attachmentCount', attachments.length.toString());
        formData.append('attachmentNames', JSON.stringify(attachments.map(a => a.name)));
        
        // console.log('email formdata with attachments', formData);
        // console.log('attachments processed:', attachments.length);

        return formData;
      } catch (error) {
        console.error('Error converting email to EML:', error);
        throw error;
      }
    }
  },
  getters: {
    currentEmail: state => state.currentEmail,
    accessToken: state => state.accessToken,
    isLoading: state => state.isLoading,
    getLoggedInUserEmailAddress: state => state.currentEmail.loggedInUser,
    getUserDetails: state => state.userDetails
  }
}

/**
 * Helper function to create EML content with HTML body and attachments
 */
function createEmlContent(emailData) {
  const boundary = `----=_NextPart_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const attachmentBoundary = `----=_AttachmentPart_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  const formatEmailAddress = (addr) => {
    if (addr.name && addr.name.trim()) {
      return `"${addr.name}" <${addr.email}>`;
    }
    return addr.email;
  };
  
  let eml = '';
  
  // Headers
  eml += `Message-ID: ${emailData.messageId}\r\n`;
  eml += `Date: ${new Date(emailData.date).toUTCString()}\r\n`;
  eml += `From: ${formatEmailAddress(emailData.from)}\r\n`;
  
  if (emailData.to.length > 0) {
    eml += `To: ${emailData.to.map(addr => formatEmailAddress(addr)).join(', ')}\r\n`;
  }
  
  if (emailData.cc.length > 0) {
    eml += `Cc: ${emailData.cc.map(addr => formatEmailAddress(addr)).join(', ')}\r\n`;
  }
  
  eml += `Subject: ${emailData.subject}\r\n`;
  eml += `MIME-Version: 1.0\r\n`;
  
  // Use different boundary structure based on whether we have attachments
  if (emailData.attachments && emailData.attachments.length > 0) {
    eml += `Content-Type: multipart/mixed; boundary="${attachmentBoundary}"\r\n`;
    eml += `\r\n`;
    
    // Email content part
    eml += `--${attachmentBoundary}\r\n`;
    eml += `Content-Type: multipart/alternative; boundary="${boundary}"\r\n`;
    eml += `\r\n`;
    
    // HTML body
    eml += `--${boundary}\r\n`;
    eml += `Content-Type: text/html; charset="utf-8"\r\n`;
    eml += `Content-Transfer-Encoding: 8bit\r\n`;
    eml += `\r\n`;
    eml += `${emailData.body}\r\n`;
    eml += `\r\n`;
    eml += `--${boundary}--\r\n`;
    
    // Add attachments
    emailData.attachments.forEach((attachment) => {
      if (attachment.content && !attachment.isItemAttachment) {
        eml += `--${attachmentBoundary}\r\n`;
        eml += `Content-Type: ${attachment.contentType}; name="${attachment.name}"\r\n`;
        eml += `Content-Transfer-Encoding: base64\r\n`;
        eml += `Content-Disposition: attachment; filename="${attachment.name}"\r\n`;
        eml += `\r\n`;
        
        // Split base64 content into 76-character lines (RFC requirement)
        const base64Lines = attachment.content.match(/.{1,76}/g) || [];
        eml += base64Lines.join('\r\n');
        eml += `\r\n`;
      } else if (attachment.isItemAttachment) {
        // Handle item attachments (embedded emails)
        eml += `--${attachmentBoundary}\r\n`;
        eml += `Content-Type: message/rfc822; name="${attachment.name}"\r\n`;
        eml += `Content-Disposition: attachment; filename="${attachment.name}"\r\n`;
        eml += `\r\n`;
        eml += `[Item attachment: ${attachment.name} - Content not accessible via API]\r\n`;
      }
    });
    
    // End attachment boundary
    eml += `--${attachmentBoundary}--\r\n`;
    
  } else {
    // No attachments, simpler structure
    eml += `Content-Type: multipart/alternative; boundary="${boundary}"\r\n`;
    eml += `\r\n`;
    
    // HTML body
    eml += `--${boundary}\r\n`;
    eml += `Content-Type: text/html; charset="utf-8"\r\n`;
    eml += `Content-Transfer-Encoding: 8bit\r\n`;
    eml += `\r\n`;
    eml += `${emailData.body}\r\n`;
    eml += `\r\n`;
    
    // End boundary
    eml += `--${boundary}--\r\n`;
  }
  
  return eml;
}
